import type { ChangeEvent, FormEvent, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { Camera, MapPin, Mic, Moon, Send, Sparkles, Square, Sun } from "lucide-react";
import Navbar from "@/components/Navbar";
import { aiApi } from "@/api/endpoints";

/* ----------------------------- static data ----------------------------- */
const EXAMPLES = [
  "Plan my weekend trip",
  "Find hidden places near me",
  "Suggest food around me",
  "Build my itinerary",
  "Ask about local culture",
];

const QUICK_ACTIONS = [
  { emoji: "🗺️", label: "Plan a Trip", prompt: "Plan a trip for me" },
  { emoji: "📍", label: "Explore Nearby", prompt: "Explore interesting places near me" },
  { emoji: "🍽️", label: "Find Restaurants", prompt: "Find good restaurants near me" },
  { emoji: "🏛️", label: "Heritage Guide", prompt: "Tell me about heritage sites in Rajasthan" },
  { emoji: "🎉", label: "Events Nearby", prompt: "What events are happening near me?" },
  { emoji: "💰", label: "Budget Planner", prompt: "Help me plan a travel budget" },
  { emoji: "🚗", label: "Route Planner", prompt: "Plan an efficient route for my trip" },
  { emoji: "🌤️", label: "Weather", prompt: "What's the weather like for travelling this weekend?" },
];

const FEATURES = [
  { emoji: "🧭", title: "Smart Trip Planner", desc: "Generate complete, day-by-day itineraries in seconds." },
  { emoji: "💎", title: "Local Discovery", desc: "Recommend hidden gems that locals actually love." },
  { emoji: "🗣️", title: "AI Translator", desc: "Translate local languages on the go." },
  { emoji: "🧮", title: "Expense Calculator", desc: "Estimate your travel budget accurately." },
  { emoji: "🚑", title: "Emergency Assistance", desc: "Nearby hospitals, police and helplines, fast." },
  { emoji: "🧳", title: "Packing Checklist", desc: "Auto-generate a checklist tailored to your trip." },
  { emoji: "💡", title: "Local Tips", desc: "Know the local do's and don'ts before you go." },
  { emoji: "✨", title: "AI Recommendations", desc: "Personalised picks based on your history." },
];

const RECENT = ["Jaipur Trip", "Udaipur Weekend", "Solo Trip", "Budget Planner"];
const TRENDING = [
  "Best places in Jaipur?",
  "Weekend trips near Delhi?",
  "Monsoon destinations?",
  "Best street food?",
];

type Msg = { role: "user" | "ai"; text: string };

/* Injected once — theme variables, animated gradient, glass, and keyframes. */
const AI_CSS = `
.ai-page { color: var(--ai-text); background: linear-gradient(-45deg, var(--ai-bg1), var(--ai-bg2), var(--ai-bg3), var(--ai-bg1)); background-size: 400% 400%; animation: aiGradient 20s ease infinite; transition: color .4s ease; }
.ai-page[data-theme="dark"] { --ai-bg1:#0f172a; --ai-bg2:#1e1b4b; --ai-bg3:#0f172a; --ai-text:#ffffff; --ai-soft:rgba(255,255,255,.68); --ai-glass:rgba(255,255,255,.06); --ai-glass2:rgba(255,255,255,.12); --ai-border:rgba(255,255,255,.14); --ai-bubble:rgba(255,255,255,.08); }
.ai-page[data-theme="light"] { --ai-bg1:#eef2ff; --ai-bg2:#faf5ff; --ai-bg3:#e0f2fe; --ai-text:#0f172a; --ai-soft:rgba(10,28,61,.62); --ai-glass:rgba(255,255,255,.55); --ai-glass2:rgba(255,255,255,.8); --ai-border:rgba(10,28,61,.10); --ai-bubble:rgba(255,255,255,.7); }
.ai-soft { color: var(--ai-soft); }
.ai-glass { background: var(--ai-glass); border: 1px solid var(--ai-border); backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px); }
.ai-glass-strong { background: var(--ai-glass2); border: 1px solid var(--ai-border); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px); }
.ai-hoverable { transition: transform .25s ease, background-color .25s ease, box-shadow .25s ease; }
.ai-hoverable:hover { background: var(--ai-glass2); transform: translateY(-4px); box-shadow: 0 16px 40px rgba(0,0,0,.18); }
.ai-page input::placeholder { color: var(--ai-soft); }
@keyframes aiGradient { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
@keyframes aiMarquee { from{transform:translateX(0)} to{transform:translateX(-50%)} }
.ai-marquee { display:flex; width:max-content; animation: aiMarquee 26s linear infinite; }
.ai-marquee:hover { animation-play-state: paused; }
@keyframes aiWave { 0%,100%{transform:scaleY(.25)} 50%{transform:scaleY(1)} }
.ai-wave span { display:block; width:3px; height:18px; border-radius:3px; background: currentColor; transform-origin:center; animation: aiWave .9s ease-in-out infinite; }
@keyframes aiBlink { 0%,100%{opacity:1} 50%{opacity:0} }
.ai-cursor { animation: aiBlink 1s step-end infinite; }
@keyframes aiDot { 0%,80%,100%{opacity:.25;transform:translateY(0)} 40%{opacity:1;transform:translateY(-4px)} }
.ai-dot { animation: aiDot 1.2s ease-in-out infinite; }
@keyframes aiRise { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
.ai-rise { animation: aiRise .35s ease both; }
@media (prefers-reduced-motion: reduce){ .ai-page,.ai-marquee,.ai-wave span,.ai-dot,.ai-rise{ animation: none !important; } }
`;

/* -------------------------------- page ---------------------------------- */
export default function AIGuide() {
  const [theme, setTheme] = useState<"dark" | "light">("dark");
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [pending, setPending] = useState(false);
  const [listening, setListening] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const recognitionRef = useRef<{ stop: () => void } | null>(null);

  const chatMode = messages.length > 0;

  useEffect(() => {
    if (chatMode) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, pending, chatMode]);

  const submit = async (raw: string) => {
    const q = raw.trim();
    if (!q || pending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setPending(true);
    try {
      const res = await aiApi.chat(q);
      setMessages((m) => [...m, { role: "ai", text: res.data.reply }]);
    } catch {
      setMessages((m) => [
        ...m,
        { role: "ai", text: "Sorry — I couldn't reach the AI service just now. Please try again in a moment." },
      ]);
    } finally {
      setPending(false);
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit(input);
  };

  const focusComposer = () => {
    inputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => inputRef.current?.focus(), 250);
  };

  /* voice: use Web Speech API when available, else a visual-only fallback */
  const toggleVoice = () => {
    const SR =
      (window as unknown as { SpeechRecognition?: new () => unknown; webkitSpeechRecognition?: new () => unknown })
        .SpeechRecognition ||
      (window as unknown as { webkitSpeechRecognition?: new () => unknown }).webkitSpeechRecognition;
    if (!SR) {
      setListening((v) => !v);
      return;
    }
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const rec = new (SR as new () => Record<string, unknown>)() as Record<string, unknown> & {
      start: () => void;
      stop: () => void;
    };
    rec.lang = "en-IN";
    rec.interimResults = true;
    (rec as Record<string, unknown>).onresult = (e: {
      results: ArrayLike<ArrayLike<{ transcript: string }>>;
    }) => {
      const t = Array.from(e.results).map((r) => r[0].transcript).join("");
      setInput(t);
    };
    (rec as Record<string, unknown>).onend = () => setListening(false);
    recognitionRef.current = rec;
    rec.start();
    setListening(true);
  };

  const shareLocation = () => {
    if (!navigator.geolocation) {
      setInput("Find hidden places near me");
      focusComposer();
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        submit(
          `Suggest interesting places near me (around ${pos.coords.latitude.toFixed(3)}, ${pos.coords.longitude.toFixed(3)}).`,
        ),
      () => {
        setInput("Find hidden places near me");
        focusComposer();
      },
    );
  };

  const onImage = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) submit(`I've uploaded a photo (${f.name}). Can you tell me about this place and what to do nearby?`);
    e.target.value = "";
  };

  /* --------------------------- render helpers --------------------------- */
  const composer = (
    <form onSubmit={onSubmit} className="ai-glass-strong flex items-center gap-2 rounded-full px-3 py-2 shadow-xl sm:gap-3 sm:px-4 sm:py-2.5">
      <Sparkles size={20} className="ml-1 shrink-0 text-gold-400" />
      <input
        ref={inputRef}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder="Ask me anything..."
        className="min-w-0 flex-1 bg-transparent py-2 text-base outline-none sm:text-lg"
      />
      {listening ? (
        <span className="ai-wave flex items-end gap-[3px] px-1 text-gold-400" aria-hidden>
          {Array.from({ length: 5 }).map((_, i) => (
            <span key={i} style={{ animationDelay: `${i * 0.12}s` }} />
          ))}
        </span>
      ) : null}
      <div className="flex items-center gap-1">
        <IconBtn label={listening ? "Stop" : "Voice input"} onClick={toggleVoice} active={listening}>
          {listening ? <Square size={17} /> : <Mic size={18} />}
        </IconBtn>
        <IconBtn label="Upload image" onClick={() => fileRef.current?.click()}>
          <Camera size={18} />
        </IconBtn>
        <IconBtn label="Share location" onClick={shareLocation}>
          <MapPin size={18} />
        </IconBtn>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={onImage} />
      </div>
      <button
        type="submit"
        disabled={pending || !input.trim()}
        className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-iris-500 text-white transition hover:bg-iris-600 disabled:opacity-40"
        aria-label="Send"
      >
        <Send size={18} />
      </button>
    </form>
  );

  return (
    <>
      <style>{AI_CSS}</style>
      <Navbar />

      <div className="ai-page min-h-screen" data-theme={theme}>
        <div className="container-page pb-28 pt-24">
          {/* Theme toggle */}
          <div className="mb-4 flex justify-end">
            <button
              onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
              className="ai-glass flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? <Sun size={14} /> : <Moon size={14} />}
              {theme === "dark" ? "Light" : "Dark"} mode
            </button>
          </div>

          {/* Hero */}
          <section className={`text-center ${chatMode ? "mb-6" : "mb-8"}`}>
            <span className="ai-glass mx-auto mb-5 inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-medium">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-gold-400" />
              </span>
              Powered by Maayad AI
            </span>
            <h1 className="font-display text-4xl font-bold tracking-tight sm:text-6xl">
              Meet{" "}
              <span className="bg-gradient-to-r from-gold-400 via-iris-400 to-jade-400 bg-clip-text text-transparent">
                Maayad AI
              </span>
            </h1>
            {!chatMode && (
              <>
                <p className="mx-auto mt-4 max-w-2xl text-lg font-medium sm:text-xl">
                  Your Intelligent Guide for Everyday Experiences
                </p>
                <p className="ai-soft mx-auto mt-2 max-w-xl text-sm sm:text-base">
                  Plan smarter, discover better, and get instant assistance — all in one place.
                </p>
              </>
            )}
          </section>

          {/* Search / composer */}
          <div className="mx-auto max-w-3xl">{composer}</div>

          {chatMode ? (
            /* ------------------------- Chat interface ------------------------ */
            <div className="mx-auto mt-6 max-w-3xl">
              <div className="ai-glass max-h-[62vh] min-h-[320px] overflow-y-auto rounded-3xl p-4 sm:p-6">
                <div className="space-y-4">
                  {messages.map((m, i) =>
                    m.role === "user" ? (
                      <div key={i} className="ai-rise flex justify-end">
                        <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-iris-500 px-4 py-2.5 text-sm text-white shadow-md">
                          {m.text}
                        </div>
                      </div>
                    ) : (
                      <div key={i} className="ai-rise flex items-start gap-2.5">
                        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-gradient-to-br from-gold-400 to-iris-500 text-white">
                          <Sparkles size={15} />
                        </span>
                        <div className="ai-glass-strong max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-tl-sm px-4 py-2.5 text-sm">
                          {/* animate only the most recent AI message */}
                          {i === messages.length - 1 ? <TypingText text={m.text} /> : m.text}
                        </div>
                      </div>
                    ),
                  )}
                  {pending && (
                    <div className="ai-rise flex items-start gap-2.5">
                      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-gradient-to-br from-gold-400 to-iris-500 text-white">
                        <Sparkles size={15} />
                      </span>
                      <div className="ai-glass-strong flex items-center gap-1.5 rounded-2xl rounded-tl-sm px-4 py-3">
                        {[0, 1, 2].map((d) => (
                          <span key={d} className="ai-dot h-2 w-2 rounded-full bg-current" style={{ animationDelay: `${d * 0.18}s` }} />
                        ))}
                      </div>
                    </div>
                  )}
                  <div ref={bottomRef} />
                </div>
              </div>
              <div className="mt-3 text-center">
                <button
                  onClick={() => setMessages([])}
                  className="ai-soft text-xs font-medium underline-offset-4 hover:underline"
                >
                  Start a new conversation
                </button>
              </div>
            </div>
          ) : (
            /* --------------------------- Landing view ------------------------ */
            <>
              {/* Rotating carousel of examples */}
              <div className="mx-auto mt-5 max-w-4xl overflow-hidden">
                <div className="ai-marquee gap-3">
                  {[...EXAMPLES, ...EXAMPLES].map((ex, i) => (
                    <button
                      key={i}
                      onClick={() => submit(ex)}
                      className="ai-glass ai-hoverable whitespace-nowrap rounded-full px-4 py-2 text-sm"
                    >
                      <span className="ai-soft">Try:</span> {ex}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quick actions */}
              <Section title="Quick Actions" className="mt-14">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {QUICK_ACTIONS.map((a) => (
                    <button
                      key={a.label}
                      onClick={() => submit(a.prompt)}
                      className="ai-glass ai-hoverable flex flex-col items-center gap-2 rounded-2xl px-3 py-5 text-center"
                    >
                      <span className="text-3xl">{a.emoji}</span>
                      <span className="text-sm font-medium">{a.label}</span>
                    </button>
                  ))}
                </div>
              </Section>

              {/* AI features hub */}
              <Section title="AI Features Hub" className="mt-14">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {FEATURES.map((f) => (
                    <div key={f.title} className="ai-glass ai-hoverable rounded-2xl p-5">
                      <span className="text-3xl">{f.emoji}</span>
                      <h3 className="mt-3 font-display text-base font-semibold">{f.title}</h3>
                      <p className="ai-soft mt-1 text-sm leading-relaxed">{f.desc}</p>
                    </div>
                  ))}
                </div>
              </Section>

              {/* User context */}
              <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-2">
                <div className="ai-glass rounded-3xl p-6">
                  <h3 className="mb-4 font-display text-lg font-semibold">Recent Conversations</h3>
                  <ul className="space-y-2">
                    {RECENT.map((r) => (
                      <li key={r}>
                        <button
                          onClick={() => submit(`Continue helping me with: ${r}`)}
                          className="ai-hoverable flex w-full items-center gap-3 rounded-xl border border-transparent px-3 py-2.5 text-left text-sm"
                        >
                          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-iris-500/20 text-base">💬</span>
                          {r}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="ai-glass rounded-3xl p-6">
                  <h3 className="mb-4 font-display text-lg font-semibold">Trending Questions</h3>
                  <div className="flex flex-wrap gap-2.5">
                    {TRENDING.map((q) => (
                      <button
                        key={q}
                        onClick={() => submit(q)}
                        className="ai-glass ai-hoverable rounded-full px-4 py-2 text-sm"
                      >
                        🔥 {q}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Floating action button */}
        <button
          onClick={focusComposer}
          className="group fixed bottom-6 right-6 z-40 flex items-center gap-0 overflow-hidden rounded-full bg-iris-500 py-4 pl-4 pr-4 text-white shadow-iris transition-all hover:bg-iris-600 hover:pr-5"
          aria-label="Ask Maayad AI"
        >
          <span className="text-xl leading-none">💬</span>
          <span className="max-w-0 overflow-hidden whitespace-nowrap text-sm font-semibold transition-all duration-300 group-hover:ml-2 group-hover:max-w-[160px]">
            Ask Maayad AI
          </span>
        </button>
      </div>
    </>
  );
}

/* ------------------------------ small parts ----------------------------- */
function Section({ title, className = "", children }: { title: string; className?: string; children: ReactNode }) {
  return (
    <section className={className}>
      <h2 className="mb-4 font-display text-xl font-bold sm:text-2xl">{title}</h2>
      {children}
    </section>
  );
}

function IconBtn({
  label,
  onClick,
  active,
  children,
}: {
  label: string;
  onClick: () => void;
  active?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={`grid h-9 w-9 place-items-center rounded-full transition ${
        active ? "bg-gold-400/20 text-gold-400" : "ai-soft hover:bg-black/5"
      }`}
    >
      {children}
    </button>
  );
}

function TypingText({ text }: { text: string }) {
  const [shown, setShown] = useState("");
  useEffect(() => {
    setShown("");
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setShown(text.slice(0, i));
      if (i >= text.length) clearInterval(id);
    }, 12);
    return () => clearInterval(id);
  }, [text]);
  return (
    <span>
      {shown}
      {shown.length < text.length && <span className="ai-cursor">▋</span>}
    </span>
  );
}